import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  Checkbox,
  CircularProgress,
  Link,
  Chip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getAllDepartments } from "../../../../../Redux/Actions/HR";
import api from "../../../../../Utils/interceptors";
import { errorNotify, successNotify } from "../../../../../Utils/toast";

const Policies = () => {
  const dispatch = useDispatch();
  const departmentsState = useSelector((state) => state.departments);
  const deptLoading =
    typeof departmentsState === "object" && departmentsState !== null
      ? Boolean(departmentsState.loading)
      : false;
  const deptList =
    typeof departmentsState === "object" && departmentsState !== null
      ? departmentsState.departments || []
      : [];

  const [handbookType, setHandbookType] = useState("sales");
  const [selectedDeptIds, setSelectedDeptIds] = useState([]);
  const [fileName, setFileName] = useState("");
  const [handbookDoc, setHandbookDoc] = useState("");
  const [uploading, setUploading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);
  const [editDeptIds, setEditDeptIds] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    dispatch(getAllDepartments("", ""));
  }, [dispatch]);

  const loadVersions = async () => {
    try {
      setLoadingVersions(true);
      const { data } = await api.get("ehb/versions");
      setVersions(data.handbookVersions || []);
    } catch (err) {
      errorNotify(err.response?.data?.message || "Failed to load handbook versions");
      setVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  };

  useEffect(() => {
    loadVersions();
  }, []);

  const toggleDepartment = (id) => {
    const sid = String(id);
    setSelectedDeptIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!handbookDoc) {
      errorNotify("Please choose a PDF or DOCX file.");
      return;
    }
    if (selectedDeptIds.length === 0) {
      errorNotify("Select at least one department.");
      return;
    }

    try {
      setUploading(true);
      await api.post("ehb/upload", {
        handbookType,
        departments: selectedDeptIds,
        handbook_doc: handbookDoc,
        fileName,
      });
      successNotify("Handbook uploaded and indexed.");
      setHandbookDoc("");
      setFileName("");
      setSelectedDeptIds([]);
      loadVersions();
    } catch (err) {
      errorNotify(err.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openEditDepartments = (version) => {
    const ids =
      (version?.departments || []).map((d) => String(d?._id || d)).filter(Boolean) || [];
    setEditingVersion(version);
    setEditDeptIds(ids);
    setEditOpen(true);
  };

  const toggleEditDepartment = (id) => {
    const sid = String(id);
    setEditDeptIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  };

  const saveEditDepartments = async () => {
    if (!editingVersion?._id) return;
    if (editDeptIds.length === 0) {
      errorNotify("Select at least one department.");
      return;
    }

    try {
      setSavingEdit(true);
      await api.patch(`ehb/${editingVersion._id}/departments`, {
        departments: editDeptIds,
      });
      successNotify("Departments updated.");
      setEditOpen(false);
      setEditingVersion(null);
      setEditDeptIds([]);
      loadVersions();
    } catch (err) {
      errorNotify(err.response?.data?.message || "Failed to update departments");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <Paper
      className="hrm_list"
      sx={{
        p: 3,
        minHeight: "70vh",
        borderRadius: "20px",
        border: "1px solid #E7EEF7",
        boxShadow: "none",
        overflow: "hidden",
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Employee handbooks (Policies)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a sales or production handbook and assign it to departments. Each upload is kept as
        history so the AI can compare policies over time. Supported: PDF, DOCX.
      </Typography>

      <Box component="form" onSubmit={handleUpload} sx={{ mb: 4 }}>
        <FormControl fullWidth sx={{ mb: 2, maxWidth: 360 }}>
          <InputLabel id="hb-type-label">Handbook type</InputLabel>
          <Select
            labelId="hb-type-label"
            value={handbookType}
            label="Handbook type"
            onChange={(e) => setHandbookType(e.target.value)}
          >
            <MenuItem value="sales">Sales</MenuItem>
            <MenuItem value="production">Production</MenuItem>
          </Select>
        </FormControl>

        <FormLabel component="legend" sx={{ mb: 1 }}>
          Departments this handbook applies to
        </FormLabel>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 220, overflow: "auto" }}>
          {deptLoading ? (
            <CircularProgress size={24} />
          ) : (
            <FormGroup>
              {deptList.map((d) => (
                <FormControlLabel
                  key={d._id}
                  control={
                    <Checkbox
                      checked={selectedDeptIds.includes(String(d._id))}
                      onChange={() => toggleDepartment(d._id)}
                    />
                  }
                  label={d.department_name}
                />
              ))}
            </FormGroup>
          )}
        </Paper>

        <Button variant="outlined" component="label" sx={{ mr: 2, mb: 2 }}>
          Choose file
          <Box
            component="input"
            type="file"
            hidden
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              if (!f) return;
              setFileName(f.name || "");
              const reader = new FileReader();
              reader.onload = () => {
                if (reader.readyState === 2) {
                  setHandbookDoc(typeof reader.result === "string" ? reader.result : "");
                }
              };
              reader.readAsDataURL(f);
            }}
          />
        </Button>
        {fileName && (
          <Typography component="span" variant="body2">
            {fileName}
          </Typography>
        )}

        <Box sx={{ mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={uploading}
            sx={{ backgroundColor: "#0097d9" }}
          >
            {uploading ? <CircularProgress size={22} color="inherit" /> : "Upload & index"}
          </Button>
        </Box>
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Handbook versions (newest first)
      </Typography>
      {loadingVersions && <CircularProgress size={28} />}
      {!loadingVersions && versions.length === 0 && (
        <Typography color="text.secondary">No handbooks uploaded yet.</Typography>
      )}
      {!loadingVersions && versions.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {versions.map((v) => {
            const url = v.file?.[0]?.url;
            return (
              <Paper key={v._id} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 1 }}>
                  <Chip label={v.handbookType} size="small" color="primary" variant="outlined" />
                  <Typography variant="body2" color="text.secondary">
                    {v.createdAt ? new Date(v.createdAt).toLocaleString() : ""}
                  </Typography>
                  {v.ingestStatus === "ok" && (
                    <Chip label={`${v.chunkCount || 0} chunks`} size="small" />
                  )}
                  {v.ingestStatus === "error" && (
                    <Chip label="Index error" size="small" color="error" />
                  )}
                  <Button size="small" variant="text" onClick={() => openEditDepartments(v)}>
                    Edit departments
                  </Button>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Departments:{" "}
                  {(v.departments || [])
                    .map((d) => d.department_name || d)
                    .filter(Boolean)
                    .join(", ") || "—"}
                </Typography>
                {url && (
                  <Link href={url} target="_blank" rel="noopener noreferrer">
                    View document
                  </Link>
                )}
                {!url && <Typography variant="caption">No file URL</Typography>}
              </Paper>
            );
          })}
        </Box>
      )}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit handbook departments</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Changing departments updates who can access this handbook in the AI chat. No re-upload needed.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, maxHeight: 320, overflow: "auto" }}>
            {deptLoading ? (
              <CircularProgress size={24} />
            ) : (
              <FormGroup>
                {deptList.map((d) => (
                  <FormControlLabel
                    key={d._id}
                    control={
                      <Checkbox
                        checked={editDeptIds.includes(String(d._id))}
                        onChange={() => toggleEditDepartment(d._id)}
                      />
                    }
                    label={d.department_name}
                  />
                ))}
              </FormGroup>
            )}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditOpen(false);
              setEditingVersion(null);
              setEditDeptIds([]);
            }}
            disabled={savingEdit}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={saveEditDepartments} disabled={savingEdit}>
            {savingEdit ? <CircularProgress size={22} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Policies;
